import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Banknote,
  BarChart3,
  ClipboardList,
  FileText,
  Megaphone,
  Package,
  PlugZap,
  ScrollText,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusVariant } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useApi } from '@/lib/api/useApi';
import { formatCents, relativeTime, titleCase } from '@/lib/format';

/**
 * Frontend widget registry. Each entry maps a widget catalog `widget_key`
 * (the same value persisted in `widget_catalog.key` server-side) to the React
 * component that renders it. Widgets are self-contained: each fetches its own
 * slice via useApi and renders a live tile. See docs/architecture.md §3.
 */

interface WidgetProps {
  title: string;
}

// ---- response shapes (subset of the fields the widgets render) -------------

interface OverviewResp {
  overview: {
    windowDays: number;
    scope: { storeCount: number };
    tasks: { open: number; completed: number; completionRatePct: number };
    incidents: { openTotal: number; bySeverity: { critical: number; high: number } };
    cash: { submittedCount: number; approvedSumCents: number };
    inventory: { submittedCount: number; approvedSumCents: number };
  };
}
interface CashListResp {
  requests: { id: string; amountCents: number; createdAt: string }[];
}
interface TasksResp {
  tasks: { id: string; title: string }[];
}
interface IncidentsResp {
  incidents: { id: string; title: string; severity: string }[];
}
interface AnnouncementsResp {
  announcements: { id: string; title: string; pinned: boolean; createdAt: string }[];
}
interface AuditResp {
  events: { id: string; action: string; createdAt: string }[];
}

// ---- shared chrome ---------------------------------------------------------

function WidgetShell({
  title,
  icon: Icon,
  link,
  linkLabel,
  children,
}: {
  title: string;
  icon: LucideIcon;
  link?: string;
  linkLabel?: string;
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 pt-1">{children}</CardContent>
      {link ? (
        <CardFooter className="py-2.5">
          <Link
            to={link}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:underline"
          >
            {linkLabel ?? 'Open'}
            <ArrowRight className="size-3" aria-hidden />
          </Link>
        </CardFooter>
      ) : null}
    </Card>
  );
}

function WidgetSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-7 w-16" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

function BigStat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div>
      <p className="text-3xl font-semibold tabular-nums tracking-tight">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function Metric({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

// ---- widgets ---------------------------------------------------------------

function PendingApprovalsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<CashListResp>('/cash-requests?status=submitted');
  const rows = data?.requests ?? [];
  return (
    <WidgetShell title={title} icon={Wallet} link="/cash-requests" linkLabel="Review cash">
      {loading ? (
        <WidgetSkeleton />
      ) : (
        <>
          <BigStat
            value={rows.length}
            label={`cash request${rows.length === 1 ? '' : 's'} awaiting approval`}
          />
          <ul className="mt-3 space-y-1.5">
            {rows.slice(0, 3).map((r) => (
              <li key={r.id} className="flex items-center justify-between text-sm">
                <span className="font-medium tabular-nums">{formatCents(r.amountCents)}</span>
                <span className="text-xs text-muted-foreground">{relativeTime(r.createdAt)}</span>
              </li>
            ))}
            {rows.length === 0 ? <EmptyLine>All caught up — nothing pending.</EmptyLine> : null}
          </ul>
        </>
      )}
    </WidgetShell>
  );
}

function TaskListWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<TasksResp>('/tasks?status=open');
  const rows = data?.tasks ?? [];
  return (
    <WidgetShell title={title} icon={ClipboardList} link="/tasks" linkLabel="Open task list">
      {loading ? (
        <WidgetSkeleton />
      ) : (
        <>
          <BigStat value={rows.length} label={`open task${rows.length === 1 ? '' : 's'}`} />
          <ul className="mt-3 space-y-1.5">
            {rows.slice(0, 3).map((t) => (
              <li key={t.id} className="truncate text-sm">
                {t.title}
              </li>
            ))}
            {rows.length === 0 ? <EmptyLine>No open tasks in your scope.</EmptyLine> : null}
          </ul>
        </>
      )}
    </WidgetShell>
  );
}

function OpenIncidentsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<IncidentsResp>('/incidents?status=open');
  const rows = data?.incidents ?? [];
  return (
    <WidgetShell title={title} icon={AlertTriangle} link="/incidents" linkLabel="Open incidents">
      {loading ? (
        <WidgetSkeleton />
      ) : (
        <>
          <BigStat value={rows.length} label={`open incident${rows.length === 1 ? '' : 's'}`} />
          <ul className="mt-3 space-y-1.5">
            {rows.slice(0, 3).map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{i.title}</span>
                <Badge variant={statusVariant(i.severity)}>{titleCase(i.severity)}</Badge>
              </li>
            ))}
            {rows.length === 0 ? <EmptyLine>No open incidents. </EmptyLine> : null}
          </ul>
        </>
      )}
    </WidgetShell>
  );
}

function CashRequestsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<OverviewResp>('/reports/overview');
  const c = data?.overview.cash;
  return (
    <WidgetShell title={title} icon={Banknote} link="/cash-requests" linkLabel="Open cash requests">
      {loading || !c ? (
        <WidgetSkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Metric value={c.submittedCount} label="Submitted (7d)" />
          <Metric value={formatCents(c.approvedSumCents)} label="Approved (7d)" />
        </div>
      )}
    </WidgetShell>
  );
}

function InventoryRequestsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<OverviewResp>('/reports/overview');
  const inv = data?.overview.inventory;
  return (
    <WidgetShell
      title={title}
      icon={Package}
      link="/inventory-requests"
      linkLabel="Open inventory requests"
    >
      {loading || !inv ? (
        <WidgetSkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Metric value={inv.submittedCount} label="Submitted (7d)" />
          <Metric value={formatCents(inv.approvedSumCents)} label="Approved (7d)" />
        </div>
      )}
    </WidgetShell>
  );
}

function StoreHealthWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<OverviewResp>('/reports/overview');
  const o = data?.overview;
  return (
    <WidgetShell title={title} icon={Activity} link="/reports" linkLabel="View reports">
      {loading || !o ? (
        <WidgetSkeleton />
      ) : (
        <div className="grid grid-cols-3 gap-2 text-center">
          <Metric value={`${o.tasks.completionRatePct}%`} label="Tasks done" />
          <Metric value={o.incidents.openTotal} label="Open incidents" />
          <Metric value={o.scope.storeCount} label="Stores" />
        </div>
      )}
    </WidgetShell>
  );
}

function ReportsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<OverviewResp>('/reports/overview');
  const o = data?.overview;
  return (
    <WidgetShell title={title} icon={BarChart3} link="/reports" linkLabel="Open reports">
      {loading || !o ? (
        <WidgetSkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Metric value={o.tasks.completed} label="Tasks completed" />
          <Metric value={o.incidents.openTotal} label="Open incidents" />
          <Metric value={formatCents(o.cash.approvedSumCents)} label="Cash approved" />
          <Metric value={`${o.tasks.completionRatePct}%`} label="Completion" />
        </div>
      )}
    </WidgetShell>
  );
}

function AnnouncementsWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<AnnouncementsResp>('/announcements');
  const rows = [...(data?.announcements ?? [])].sort((a, b) => Number(b.pinned) - Number(a.pinned));
  return (
    <WidgetShell title={title} icon={Megaphone} link="/announcements" linkLabel="See announcements">
      {loading ? (
        <WidgetSkeleton />
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 2).map((a) => (
            <li key={a.id} className="space-y-0.5">
              <div className="flex items-center gap-2">
                {a.pinned ? <Badge variant="warning">Pinned</Badge> : null}
                <span className="truncate text-sm font-medium">{a.title}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">{relativeTime(a.createdAt)}</p>
            </li>
          ))}
          {rows.length === 0 ? <EmptyLine>No announcements yet.</EmptyLine> : null}
        </ul>
      )}
    </WidgetShell>
  );
}

function AuditLogWidget({ title }: WidgetProps) {
  const { data, loading } = useApi<AuditResp>('/audit-log?limit=5');
  const rows = data?.events ?? [];
  return (
    <WidgetShell title={title} icon={ScrollText} link="/admin/audit" linkLabel="Open audit log">
      {loading ? (
        <WidgetSkeleton />
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 5).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{titleCase(e.action)}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {relativeTime(e.createdAt)}
              </span>
            </li>
          ))}
          {rows.length === 0 ? <EmptyLine>No recent activity.</EmptyLine> : null}
        </ul>
      )}
    </WidgetShell>
  );
}

function DocumentsWidget({ title }: WidgetProps) {
  return (
    <WidgetShell title={title} icon={FileText} link="/documents" linkLabel="Browse documents">
      <p className="text-sm text-muted-foreground">SOPs, runbooks, and policies for your stores.</p>
    </WidgetShell>
  );
}

function NotConnectedWidget({ title, icon }: WidgetProps & { icon: LucideIcon }) {
  return (
    <WidgetShell title={title} icon={icon}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <PlugZap className="size-4" aria-hidden />
        Not connected yet
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Toast / 7shifts integration pending.</p>
    </WidgetShell>
  );
}

function SalesTodayWidget({ title }: WidgetProps) {
  return <NotConnectedWidget title={title} icon={BarChart3} />;
}
function LaborVarianceWidget({ title }: WidgetProps) {
  return <NotConnectedWidget title={title} icon={Users} />;
}

export const widgetRegistry: Record<string, ComponentType<WidgetProps>> = {
  task_list: TaskListWidget,
  open_incidents: OpenIncidentsWidget,
  sales_today: SalesTodayWidget,
  labor_variance: LaborVarianceWidget,
  pending_approvals: PendingApprovalsWidget,
  cash_requests: CashRequestsWidget,
  inventory_requests: InventoryRequestsWidget,
  store_health: StoreHealthWidget,
  announcements: AnnouncementsWidget,
  documents: DocumentsWidget,
  audit_log: AuditLogWidget,
  reports: ReportsWidget,
};

export function getWidget(key: string): ComponentType<WidgetProps> | null {
  return widgetRegistry[key] ?? null;
}
